import React, { useEffect, useState, useRef, useCallback } from "react";
import { FileUpload } from "../FileUpload";
import { FilePicker } from "../FilePicker";
import { CreatePostModal } from "../CreatePostModal";
import {
  loadFromCache,
  queuePageLoad,
  clearCache,
  markPagesAsLoaded,
  saveToCache,
} from "../../../utils/fileLoadingQueue";

import "./styles.css";
import {
  AddFiles,
  PostSelection,
  SelectedFile,
  SetSelectedPost,
  UpdateFile,
  UpdatePosts,
} from "../../../types/PostSelection";
import { Post } from "../../../types/api/Post";
import { useWindowResizeThreshold } from "../../../state/hooks/useWindowResizeThreshold";

// Export function to clear cache externally (useful for forced refresh)
export const clearFilesCache = clearCache;

export function MediaPane({
  updatePosts,
  selectedPost,
  setSelectedPost,
}: {
  updatePosts: UpdatePosts;
  selectedPost: Post | null;
  setSelectedPost: SetSelectedPost;
}) {
  const isMobileSize = useWindowResizeThreshold();
  // Initialize state with cached data if available
  const cachedData = loadFromCache();
  const [files, setFiles] = useState<SelectedFile[]>(cachedData?.files || []);
  const [hasMore, setHasMore] = useState(cachedData?.hasMore ?? true);
  const [isLoading, setIsLoading] = useState(false);
  const [currentFolder, setCurrentFolder] = useState<string>("");
  const [selection, setSelection] = useState<PostSelection>({
    openModal: false,
    selected: [],
    update: false,
    step: 1,
  });
  const [pageNumber, setPageNumber] = useState(cachedData?.pageNumber ?? -1);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [showFolderSelectModal, setShowFolderSelectModal] = useState(false);
  const [initialLoading, setInitialLoading] = useState(
    !cachedData || cachedData?.files.length === 0
  );

  const addFiles: AddFiles = (filesToAdd: SelectedFile[]) => {
    setFiles((f) => {
      let files = [...f];

      for (const fileToAdd of filesToAdd) {
        const index = files.findIndex((fi) => fi.name === fileToAdd.name);
        if (index > -1) {
          files = [
            ...files.slice(0, index),
            fileToAdd,
            ...files.slice(index + 1),
          ];
        } else {
          files = [fileToAdd, ...files];
        }
      }

      // Update cache when files are added
      saveToCache(files, pageNumber, hasMore, currentFolder);
      return files;
    });
    setInitialLoading(false);
  };

  const updateFile: UpdateFile = (name, data) => {
    setFiles((allFiles) =>
      allFiles.map((f) => (f.name === name ? { ...f, ...data } : f))
    );
  };

  const resetSelectedFiles = () => {
    setFiles((allFiles) => allFiles.map((f) => ({ ...f, isSelected: false })));
  };

  const refreshFileUsage = (ids: string[]) => {
    for (const file of files) {
      if (ids.includes(file._id!)) {
        updateFile(file.name, { isUsed: true });
      }
    }
  };

  const handleFolderChange = (folderPath: string) => {
    // Clear current files and cache when changing folders
    setInitialLoading(true);
    setFiles([]);
    setPageNumber(-1);
    setHasMore(true);
    clearCache();
    setCurrentFolder(folderPath);

    // Load first page of the new folder
    queuePageLoad(
      0,
      setFiles,
      setHasMore,
      setIsLoading,
      setPageNumber,
      setInitialLoading,
      folderPath
    );
  };

  const loadFiles = useCallback(
    (page: number) => {
      queuePageLoad(
        page,
        setFiles,
        setHasMore,
        setIsLoading,
        setPageNumber,
        setInitialLoading,
        currentFolder
      );
    },
    [
      setFiles,
      setHasMore,
      setIsLoading,
      setPageNumber,
      setInitialLoading,
      currentFolder,
    ]
  );

  useEffect(() => {
    if (!cachedData) {
      loadFiles(0);
    } else {
      // Mark cached pages as loaded to prevent duplicate requests
      markPagesAsLoaded(cachedData.pageNumber);
    }
  }, [loadFiles, cachedData]);

  const observer = useRef<IntersectionObserver | null>(null);
  const lastFileElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadFiles(pageNumber + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [hasMore, pageNumber, loadFiles]
  );

  return (
    <div className="media-pane">
      {!isMobileSize && (
        <FileUpload
          addFiles={addFiles}
          updateFile={updateFile}
          isEmpty={files.length === 0 && !initialLoading}
          currentFolder={currentFolder}
          setShowCreateFolder={setShowCreateFolder}
          setShowFolderSelectModal={setShowFolderSelectModal}
          selectedCount={files?.filter((f) => f.isSelected)?.length}
          selectedFiles={files?.filter((f) => f.isSelected) || []}
          onDeleteComplete={() => handleFolderChange(currentFolder)}
        >
          <FilePicker
            files={files}
            updateFile={updateFile}
            createPost={(selected) => {
              console.log("SELECTED PHOTOS CREATE POST", selected);
              setSelection({
                openModal: true,
                selected,
                update: false,
                step: 1,
              });
            }}
            editMode={false}
            lastFileElementRef={lastFileElementRef}
            isLoading={isLoading}
            currentFolder={currentFolder}
            onFolderChange={handleFolderChange}
            showCreateFolder={showCreateFolder}
            setShowCreateFolder={setShowCreateFolder}
            showFolderSelectModal={showFolderSelectModal}
            setShowFolderSelectModal={setShowFolderSelectModal}
          />
        </FileUpload>
      )}

      {(selection.openModal || selectedPost) && (
        <CreatePostModal
          selection={selection}
          setSelection={setSelection}
          updatePosts={updatePosts}
          selectedPost={selectedPost}
          setSelectedPost={setSelectedPost}
          updateFile={updateFile}
          resetSelectedFiles={resetSelectedFiles}
          refreshFileUsage={refreshFileUsage}
          availableFiles={files}
          addFiles={addFiles}
        />
      )}
    </div>
  );
}
